// PDP Accordion Modal - Only affects pdp-accordion-custom elements
class PDPAccordionModal {
  constructor() {
    this.activeAccordion = null;
    this.init();
  }

  init() {
    // Only proceed if we're on a product page or there are pdp accordions
    const pdpAccordions = document.querySelectorAll('pdp-accordion-custom');
    if (pdpAccordions.length === 0) return;
    
    // Create modal container and overlay once
    this.createModalContainer();
    
    // Transform existing PDP accordions only
    this.transformPDPAccordions();
    
    // Handle dynamic content
    this.observeChanges();
  }

  createModalContainer() {
    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'pdp-modal-overlay';
    this.overlay.addEventListener('click', () => this.closeModal());
    document.body.appendChild(this.overlay);

    // Create modal wrapper
    this.modalWrapper = document.createElement('div');
    this.modalWrapper.className = 'pdp-modal-wrapper';
    this.modalWrapper.innerHTML = `
      <div class="pdp-modal-container">
        <div class="pdp-modal-drag-handle"></div>
        <div class="pdp-modal-header">
          <h2 class="pdp-modal-title"></h2>
          <button type="button" class="pdp-modal-close popup-close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="pdp-modal-body"></div>
      </div>
    `;
    document.body.appendChild(this.modalWrapper);

    // Cache elements
    this.modalContainer = this.modalWrapper.querySelector('.pdp-modal-container');
    this.modalTitle = this.modalWrapper.querySelector('.pdp-modal-title');
    this.modalBody = this.modalWrapper.querySelector('.pdp-modal-body');
    this.closeButton = this.modalWrapper.querySelector('.pdp-modal-close');
    
    // Close button handler
    this.closeButton.addEventListener('click', () => this.closeModal());
    
    // Escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeAccordion) {
        this.closeModal();
      }
    });
  }

  transformPDPAccordions() {
    // ONLY select pdp-accordion-custom elements, not regular accordion-custom
    const pdpAccordions = document.querySelectorAll('pdp-accordion-custom');
    
    pdpAccordions.forEach(accordion => {
      // Skip if already transformed
      if (accordion.dataset.modalTransformed) return;
      
      const details = accordion.querySelector('details');
      const summary = accordion.querySelector('summary');
      
      if (details && summary) {
        // Mark as transformed
        accordion.dataset.modalTransformed = 'true';
        
        // Override the default click behavior
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal(accordion, details, summary);
        });
        
        // Prevent default details toggle
        details.addEventListener('toggle', (e) => {
          if (details.open) {
            e.preventDefault();
            details.open = false;
            this.openModal(accordion, details, summary);
          }
        });
      }
    });
  }

  openModal(accordion, details, summary) {
    // Get the title
    const titleText = summary.textContent.trim();
    this.modalTitle.textContent = titleText;
    
    // Move the accordion content to modal
    const content = details.querySelector('.details-content');
    if (content) {
      // Store reference to original parent
      this.originalParent = content.parentNode;
      this.originalContent = content;
      
      // Move content to modal (this preserves all event listeners!)
      this.modalBody.appendChild(content);
    }
    
    // Store active accordion
    this.activeAccordion = accordion;
    
    // Show modal
    this.overlay.classList.add('active');
    this.modalWrapper.classList.add('active');
    
    // Prevent body scroll without visual jump
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    
    // Focus close button
    setTimeout(() => {
      this.closeButton.focus();
    }, 100);
  }

  closeModal() {
    if (!this.activeAccordion) return;
    
    // Move content back to original position
    if (this.originalContent && this.originalParent) {
      this.originalParent.appendChild(this.originalContent);
    }
    
    // Hide modal
    this.overlay.classList.remove('active');
    this.modalWrapper.classList.remove('active');
    
    // Restore scrolling
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    
    // Clear references
    this.activeAccordion = null;
    this.originalParent = null;
    this.originalContent = null;
    this.modalTitle.textContent = '';
  }

  observeChanges() {
    // Watch for new PDP accordions added dynamically
    const observer = new MutationObserver(() => {
      this.transformPDPAccordions();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Also need to handle the pdp-accordion-custom web component
class PDPAccordionCustom extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    // Basic accordion functionality - similar to accordion-custom
    const details = this.querySelector('details');
    const summary = this.querySelector('summary');
    
    if (!details || !summary) return;
    
    // Handle open-by-default attributes
    if (this.hasAttribute('open-by-default-on-desktop') && window.innerWidth >= 750) {
      details.open = true;
    }
    if (this.hasAttribute('open-by-default-on-mobile') && window.innerWidth < 750) {
      details.open = true;
    }
  }
}

// Register the custom element
if (!customElements.get('pdp-accordion-custom')) {
  customElements.define('pdp-accordion-custom', PDPAccordionCustom);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PDPAccordionModal();
  });
} else {
  new PDPAccordionModal();
}

// Handle Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    // Only reinitialize if there are PDP accordions
    const pdpAccordions = document.querySelectorAll('pdp-accordion-custom');
    if (pdpAccordions.length > 0) {
      const instance = new PDPAccordionModal();
      instance.transformPDPAccordions();
    }
  });
}